import React, { createContext, useContext, useState, useEffect } from "react";
import { getCookie, setCookie, deleteCookie } from "../utils/cookieUtils";
import { jwtDecode } from "jwt-decode";
import { useUserContext } from "./UserContext";
import * as Sentry from "@sentry/react"

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { updateUserId } = useUserContext();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const getPartitionedKey = (key) => {
    if (window.Office && Office.context && Office.context.partitionKey) {
      return `${Office.context.partitionKey}_${key}`;
    }
    return key;
  };

  const getPartitionedLocalStorage = (key) => {
    return localStorage.getItem(getPartitionedKey(key));
  };

  const setPartitionedLocalStorage = (key, value) => {
    localStorage.setItem(getPartitionedKey(key), value);
  };
  // Function to check authentication status (can be called externally)
  const checkAuthStatus = () => {
    let token;
    if (window.Office && Office.context && Office.context.partitionKey) {
      token = getPartitionedLocalStorage("token");
    } else {
      token = getCookie("token") || localStorage.getItem("token");
    }
    // token = getPartitionedLocalStorage("token") || getCookie("token") || localStorage.getItem("token");
    console.log("[DEBUG][AuthContext] checkAuthStatus called. Token found:", token);
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp * 1000 > Date.now()) {
          console.log("[DEBUG][AuthContext] decodedToken.exp * 1000 > Date.now() checkAuthStatus");
          setIsLoggedIn(true);
          if (decodedToken.user_id) updateUserId(decodedToken.user_id);
          return true;
        } else {
          logout();
          return false;
        }
      } catch (error) {
        console.error("Invalid token:", error);
        Sentry.captureException(error);
        logout();
        return false;
      }
    }
    return false;
  };

  useEffect(() => {
    checkAuthStatus();

    // Listen for custom authentication events (from dialogs, etc.)
    const handleAuthChange = () => {
      console.log("🔄 Auth change event received, refreshing auth state...");
      setTimeout(() => checkAuthStatus(), 100); // Small delay to ensure token is saved
    };

    window.addEventListener('authStateChanged', handleAuthChange);

    return () => {
      window.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, []);

  const login = (token, user_id) => {
    try {
      const decodedToken = jwtDecode(token);
      console.log("[DEBUG][AuthContext] login called. Token found:", token);
      console.log("[DEBUG][AuthContext] decodedToken:", decodedToken);
      if (decodedToken.exp * 1000 > Date.now()) {
        console.log("[DEBUG][AuthContext] decodedToken.exp * 1000 > Date.now()");
        setCookie("token", token);
        setPartitionedLocalStorage("token", token);
        setCookie("user_id", user_id);
        setPartitionedLocalStorage("user_id", user_id);
        localStorage.setItem("token", token); // Sync both
        setPartitionedLocalStorage("token", token);
        updateUserId(user_id);
        setIsLoggedIn(true);
      } else {
        console.error("Token has expired.");
      }
    } catch (error) {
      console.error("Invalid token:", error);
      Sentry.captureException(error);
    }
  };

  const handleGoogleLogin = () => {
    const token =  getPartitionedLocalStorage("token")|| getCookie("token") || localStorage.getItem("token");
    if (!token) return;
    try {

      const decodedToken = jwtDecode(token);
      if (decodedToken.exp * 1000 > Date.now()) {
        updateUserId(decodedToken.user_id);
        setIsLoggedIn(true);
      } else {
        logout();
      }
    } catch (error) {
      console.error("Error decoding Google token:", error);
      Sentry.captureException(error);
      logout();
    }
  };

  const logout = () => {
    deleteCookie("token");
    deleteCookie("user_id");
    localStorage.removeItem("token");
    updateUserId(null);
    setIsLoggedIn(false);
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, handleGoogleLogin, logout, checkAuthStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);